/**
 * Selectors for error modal state
 */

import type { QuotaErrorCode } from '../slices/errorModalSlice';
import type { RootState } from '../store';

/**
 * Get current error code (402, 409, etc.)
 */
export const selectErrorCode = (state: RootState): number | null =>
  state.errorModal.errorCode;

/**
 * Get the backend error message (e.g. "Daily standard limit reached (3/3)").
 * Used as a fallback detail line when structured quota data is unavailable.
 */
export const selectErrorMessage = (state: RootState): string | null =>
  state.errorModal.errorMessage;

/**
 * Get the effective quota error code for OverflowPrompt.
 */
export const selectQuotaErrorCode = (state: RootState): QuotaErrorCode | null =>
  state.errorModal.quotaErrorCode;

/**
 * Get the thinking mode that triggered the limit
 */
export const selectErrorThinkingMode = (state: RootState): string | null =>
  state.errorModal.thinkingMode;

/**
 * Check if modal was opened manually (user clicked upgrade/subscribe button)
 */
export const selectIsManualOpen = (state: RootState): boolean =>
  state.errorModal.isManualOpen;

/**
 * Get the error source (which feature triggered the error)
 */
export const selectErrorSource = (state: RootState) => state.errorModal.source;

/**
 * Get the scroll anchor (HTML id to scroll to after the modal opens).
 * Set via ``openErrorModal({ scrollAnchor: 'professional-plans' })``.
 */
export const selectScrollAnchor = (state: RootState) =>
  state.errorModal.scrollAnchor;

/**
 * Doc-gen monthly usage count / ceiling (DOC_GEN_MONTHLY_LIMIT_REACHED only).
 * Null for every other quota error code — see `ErrorModalState.docGenUsed`.
 */
export const selectDocGenUsed = (state: RootState): number | null =>
  state.errorModal.docGenUsed;

export const selectDocGenLimit = (state: RootState): number | null =>
  state.errorModal.docGenLimit;

/**
 * OverflowPrompt: shows for actual quota errors with a structured error code.
 * (DAILY_LIMIT_REACHED, MODE_NOT_AVAILABLE, MONTHLY_CAP_REACHED, etc.)
 */
export const selectShouldShowOverflowPrompt = (state: RootState): boolean => {
  const { isOpen, quotaErrorCode, isManualOpen } = state.errorModal;
  return isOpen && quotaErrorCode !== null && !isManualOpen;
};

/**
 * UpgradeModal (plan cards): shows for manual opens (crown button, trial banner)
 * and 409 concurrent limit errors.
 */
export const selectShouldShowUpgradeModal = (state: RootState): boolean => {
  const { isOpen, errorCode, isManualOpen } = state.errorModal;
  if (isManualOpen) return isOpen;
  return isOpen && errorCode === 409;
};
